import {
  IonList,
  IonText,
  IonItem,
  IonLabel,
  IonCheckbox,
  IonLoading,
  IonTitle,
} from "@ionic/react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { decodeParam } from "../util/ApiHelper";
import { TranslationMessagesContext } from "../util/Translations";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import { PermitItem } from "../models/workorders/Permit";
import useGetPermits from "../hooks/work-order/useGetPermits";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";

const PermitsTab: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  let displayWo = decodeParam(workOrderId);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [permitsErrorState, setPermitsErrorState] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const completedPermits = useWorkOrderStore(
    useShallow((state) => state.getCompletedPermitsForWo(displayWo))
  );
  const { getPermits } = useGetPermits();
  const permits = useMemo(
    () =>
      (completedPermits ?? []).reduce(
        (grouped: { [key: string]: PermitItem[] }, item) => {
          item.permitItems.forEach((permitItem) => {
            if (permitItem.IsEnabled)
              (grouped[item.Category] = grouped[item.Category] || []).push(
                permitItem
              );
          });
          return grouped;
        },
        {}
      ),
    [completedPermits]
  );

  const loadWoPermits = useCallback(async () => {
    if (completedPermits) return; //got the permits from cache
    setPermitsErrorState(false);
    const response = await getPermits(displayWo);
    if (response.status !== 200) {
      setPermitsErrorState(true);
    } else if (response.data) {
      setPermitsErrorState(false);
    }
  }, [completedPermits, displayWo, getPermits]);

  useEffect(() => {
    (async () => {
      // Show loading symbol
      setShowLoading(true);
      await loadWoPermits();
      setShowLoading(false);
    })();
  }, [loadWoPermits, workOrderId]);

  return (
    <>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
        duration={5000}
      />
      {!!permits && Object.keys(permits).length > 0 ? (
        Object.entries(permits).map(([category, permitItems], index) => {
          return (
            <div key={index}>
              <IonText className="permits-item-title">
                <h5>{category}</h5>
              </IonText>
              <IonList
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
                  gap: "40px",
                }}
              >
                {permitItems.map((item: any, index: number) => (
                  <IonItem key={index} lines="none">
                    <IonCheckbox slot="start" checked disabled></IonCheckbox>
                    <IonLabel style={{ opacity: 1, whiteSpace: "normal" }}>{item.Code}</IonLabel>
                  </IonItem>
                ))}
              </IonList>
            </div>
          );
        })
      ) : (
        <IonText
          color={permitsErrorState ? "danger" : "default"}
          style={{ padding: "16px", display: "inline-block" }}
          hidden={showLoading}
        >
          {permitsErrorState
            ? `${translatedMessages["ErrPermits"]?.MessageText ||
            "Error retrieving permits"
            }`
            : `${translatedMessages["NoResultsFound"]?.MessageText ||
            "No results found."
            }`}
        </IonText>
      )}
    </>
  );
};

export default withAITracking(reactPlugin, PermitsTab, "PermitsTab");
